import { createColumnHelper } from '@tanstack/react-table';
import { TableProvider, Table, Stack, useTableState } from '@optimus-web/core';
import { WithBillId } from '@mews-commander/types';
import { CorrectedOrderItem } from '@mews-commander/data-access';
import { LocalizedCurrencyValue, LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { LocalizedDateTimeFromUtc, LocalizedMinorHighlight } from '@mews-commander/ui';
import { UnicodeEntity } from '@mews/enums';
import { useCorrectionDetail } from '../../hooks/useCorrectionDetail';
import { CorrectionBillTotalRow } from './CorrectionBillTotalRow';
import { CorrectionBillOrderItemActionLogLink } from './CorrectionBillOrderItemActionLogLink';

const helper = createColumnHelper<CorrectedOrderItem>();

const mapping = [
    helper.accessor('name', {
        header: () => <LocalizedText textKey={TextKey.Item} />,
        cell: (props) => {
            const { name, billId, id } = props.row.original;
            return name ? (
                <CorrectionBillOrderItemActionLogLink id={id} billId={billId} name={name} />
            ) : (
                UnicodeEntity.Dash
            );
        },
    }),
    helper.accessor('consumedUtc', {
        header: () => <LocalizedText textKey={TextKey.Consumption} />,
        cell: (props) => <LocalizedDateTimeFromUtc utcString={props.getValue()} />,
    }),
    helper.accessor('unitMultiplier', {
        header: () => <LocalizedText textKey={TextKey.QuantityShortened} />,
        cell: (props) => (
            <Stack alignment="center" fill>
                {props.getValue()}
            </Stack>
        ),
    }),
    helper.accessor('totalAmount', {
        header: () => <LocalizedText textKey={TextKey.Amount} />,
        cell: (props) => {
            const totalAmount = props.getValue();

            return <LocalizedCurrencyValue value={totalAmount} />;
        },
        meta: {
            align: 'end',
        },
    }),
];

export const CorrectionBillOrderItemsTable = ({ billId }: WithBillId) => {
    const { data, isFetching } = useCorrectionDetail({ correctionBillId: billId });

    const [tableState, setTableState] = useTableState<CorrectedOrderItem>({
        pagination: {
            show: false,
        },
    });

    const { correctiveOrderItems } = data || {};

    return (
        <Stack vertical>
            <TableProvider columns={mapping} state={tableState} onStateChange={setTableState}>
                <Stack vertical spacing="300">
                    <LocalizedMinorHighlight textKey={TextKey.CorrectedItemPlural} />
                    <Table uniqueRowId="id" data={correctiveOrderItems || []} loading={isFetching} amountOfRows={4} />
                </Stack>
            </TableProvider>
            <CorrectionBillTotalRow billId={billId} />
        </Stack>
    );
};
